import appConstants from '../../../../appConstants';
import { getInternationalNumberFormat } from '../../../../utilities/appHelper';
import { convertDateTimeToTimeString } from '@dataGrid/gridUtilities';
import { CREATE_FINAL_MILE_ROUTE_PARAMETER, SINGLE_SELECTED_ROUTE_COUNT } from './constants';

export const getDynamicRoutingData = ({
  routesList,
  selectedRouteNameList,
  trailersData,
  parametersData,
  wavesData
}) => {
  const dynamicRoutingData = initializeDynamicRoutingData();
  const trailersCount = {};

  routesList.forEach((route) => {
    if (route.routeName === appConstants.UNRTD_ROUTENAME) return; // Avoid UNRTD

    updateRouteCounts(route, selectedRouteNameList, dynamicRoutingData);
    updateTrailerCounts(route, selectedRouteNameList, trailersCount);
  });

  dynamicRoutingData.trailersData = trailersData.slice();
  populateTrailerSummary(dynamicRoutingData, trailersCount);

  dynamicRoutingData.error = getDynamicRoutingDataError(dynamicRoutingData);
  dynamicRoutingData.shipmentDetails.Weight =
    getInternationalNumberFormat(dynamicRoutingData.shipmentDetails.Weight) + ' lbs';

  setOptimizationConstraints(dynamicRoutingData, parametersData, wavesData);

  dynamicRoutingData.isOnlyFMERoute = checkIfOnlyFMEroute(
    selectedRouteNameList,
    dynamicRoutingData.trailersData
  );
  dynamicRoutingData.isOnlyLinkexRoute = checkIfOnlyLinkexRoute({
    routesList: selectedRouteNameList,
    trailersData: dynamicRoutingData.trailersData,
    parametersData
  });

  return dynamicRoutingData;
};

// Initializes and returns the default structure for dynamic routing data.
const initializeDynamicRoutingData = () => ({
  totalRoutes: 0,
  totalLockedRoutes: 0,
  totalUnlockedRoutes: 0,
  totalPartialLockedRoutes: 0,
  totalTrapRoutes: 0,
  selectedRoutes: [],
  trailersData: [],
  shipmentDetails: {
    Pros: 0,
    Stops: 0,
    HU: 0,
    'LG Pros': 0,
    PSE: 0,
    Priority: 0,
    Weight: 0
  },
  optimizationConstraints: {
    expectedRouteCount: null,
    liftgateTrailerCount: null,
    maxStopsPerRouteCount: 0,
    maxLiftgateTrailerAvailable: 0,
    lastWaveCutOff: '',
    isMergeExtraRoutes: false,
    mergedExtraRouteName: ''
  },
  routesSummary: {
    selectedRoutes: { count: 0, trailers: [] },
    unselectedRoutes: { count: 0, trailers: [] }
  }
});

// Updates the counts of routes based on their selection status.
const updateRouteCounts = (route, selectedRouteNameList, dynamicRoutingData) => {
  if (selectedRouteNameList.includes(route.routeName)) {
    dynamicRoutingData.totalRoutes++;
    dynamicRoutingData.routesSummary.selectedRoutes.count++;
    dynamicRoutingData.totalLockedRoutes +=
      route.status === appConstants.routeLockStates.LOCK.value ? 1 : 0;
    dynamicRoutingData.totalUnlockedRoutes +=
      route.status === appConstants.routeLockStates.UNLOCK.value ? 1 : 0;
    dynamicRoutingData.totalPartialLockedRoutes +=
      route.status === appConstants.routeLockStates.PARTIAL_LOCK.value ? 1 : 0;
    dynamicRoutingData.totalTrapRoutes += route.isTrapRoute ? 1 : 0;
    dynamicRoutingData.selectedRoutes.push(route.routeName);
    dynamicRoutingData.shipmentDetails.Pros += route.prosCount || 0;
    dynamicRoutingData.shipmentDetails.Stops += route.deliveryStopsCount || 0;
    dynamicRoutingData.shipmentDetails.HU += route.totalHu || 0;
    dynamicRoutingData.shipmentDetails['LG Pros'] += route.lgProCount || 0;
    dynamicRoutingData.shipmentDetails.PSE += route.totalPse || 0;
    dynamicRoutingData.shipmentDetails.Priority += route.priorityProCount || 0;
    dynamicRoutingData.shipmentDetails.Weight += route.totalWeight || 0;
  } else {
    dynamicRoutingData.routesSummary.unselectedRoutes.count++;
  }
};

// Updates the trailer counts based on the route's trailer name and selection status.
const updateTrailerCounts = (route, selectedRouteNameList, trailersCount) => {
  if (route.trailerName) {
    const isSelectedRoute = selectedRouteNameList.includes(route.routeName);
    const fieldName = isSelectedRoute ? 'selected' : 'unselected'; // Set the field name based on selection status

    // Initialize the trailer count entry if it doesn't exist
    if (!trailersCount[route.trailerName]) {
      trailersCount[route.trailerName] = { selected: 0, unselected: 0 };
    }

    // Increment the count for the appropriate category (selected or unselected)
    trailersCount[route.trailerName][fieldName] += 1;
  }
};

// Populates the trailer summary in the dynamic routing data based on trailer counts.
const populateTrailerSummary = (dynamicRoutingData, trailersCount) => {
  dynamicRoutingData.trailersData.forEach((trailer) => {
    const { trailerName } = trailer;
    if (trailersCount[trailerName]) {
      if (trailersCount[trailerName].selected > 0) {
        // If there are selected routes for this trailer, add it to the selected routes summary
        dynamicRoutingData.routesSummary.selectedRoutes.trailers.push({
          trailerName,
          count: trailersCount[trailerName].selected
        });
      }
      if (trailersCount[trailerName].unselected > 0) {
        // If there are unselected routes for this trailer, add it to the unselected routes summary
        dynamicRoutingData.routesSummary.unselectedRoutes.trailers.push({
          trailerName,
          count: trailersCount[trailerName].unselected
        });
      }
    }
  });
};

// Sets optimization constraints based on parameters and wave data.
const setOptimizationConstraints = (dynamicRoutingData, parametersData, wavesData) => {
  const maxNumberOfStopsPerRoute = parametersData.find(
    (param) => param.parameterName === 'MaxNumberOfStopsPerRoute'
  );
  let waveCutOffTime = wavesData?.length > 0 ? wavesData[0].ibetaCutOffTime : null;

  wavesData.forEach((wave) => {
    waveCutOffTime =
      convertDateTimeToTimeString(waveCutOffTime) <
      convertDateTimeToTimeString(wave.ibetaCutOffTime)
        ? wave.ibetaCutOffTime
        : waveCutOffTime;
  });

  dynamicRoutingData.optimizationConstraints.maxStopsPerRouteCount = maxNumberOfStopsPerRoute
    ? maxNumberOfStopsPerRoute.scenarioValue || maxNumberOfStopsPerRoute.defaultValue
    : 0;

  dynamicRoutingData.optimizationConstraints.lastWaveCutOff =
    convertDateTimeToTimeString(waveCutOffTime);

  const isMergeExtraRoutes = parametersData.find(
    (param) => param.parameterName === 'IsMergeExtraRoutes'
  );
  dynamicRoutingData.optimizationConstraints.isMergeExtraRoutes =
    isMergeExtraRoutes?.defaultValue === '1';

  const mergedExtraRouteName = parametersData.find(
    (param) => param.parameterName === 'MergedExtraRouteName'
  );
  dynamicRoutingData.optimizationConstraints.mergedExtraRouteName =
    mergedExtraRouteName?.defaultValue ?? '';
};

// Checks for errors in the dynamic routing data.
const getDynamicRoutingDataError = (data) => {
  if (data.totalTrapRoutes) {
    return 'Trap routes cannot be optimized';
  } else if (data.totalLockedRoutes) {
    return 'Locked routes cannot be optimized';
  }
};

// Constructs a message summarizing the selected routes.
export const getSelectedRouteMessage = (data) => {
  const selectedRoutes = [];

  data.totalLockedRoutes && selectedRoutes.push(`${data.totalLockedRoutes} locked`);
  data.totalUnlockedRoutes && selectedRoutes.push(`${data.totalUnlockedRoutes} unlocked`);
  data.totalPartialLockedRoutes &&
    selectedRoutes.push(`${data.totalPartialLockedRoutes} partially locked`);

  let message = '';
  const totalRoutes =
    data.totalLockedRoutes + data.totalUnlockedRoutes + data.totalPartialLockedRoutes;

  switch (selectedRoutes.length) {
    case 1:
      message = selectedRoutes[0];
      break;
    case 2:
      message = selectedRoutes.join(' and ');
      break;
    default:
      message =
        selectedRoutes.slice(0, selectedRoutes.length - 1).join(' , ') +
        ' and ' +
        selectedRoutes[selectedRoutes.length - 1];
      break;
  }
  const pluralization = totalRoutes === 1 ? ' route is selected' : ' routes are selected';
  return message ? `${message} ${pluralization}` : '';
};

export const checkIfOnlyFMEroute = (routesList, trailersData) => {
  const isFMETrailerPresent = trailersData.some((trailer) =>
    checkIfFMERouteTrailer(trailer.trailerTypeName)
  );
  return (
    routesList?.length === 1 && routesList[0] === appConstants.FME_ROUTE_NAME && isFMETrailerPresent
  );
};

export const isCreateFinalMileRouteEnabled = (parametersData) => {
  const finalMileRouteParameter = parametersData?.find(
    (parameter) => parameter.parameterName === CREATE_FINAL_MILE_ROUTE_PARAMETER.name
  );
  const parameterValue =
    finalMileRouteParameter?.defaultValue ?? CREATE_FINAL_MILE_ROUTE_PARAMETER.fallbackValue;
  return parameterValue === CREATE_FINAL_MILE_ROUTE_PARAMETER.enabledValue;
};

export const checkIfLinkexRouteTrailer = (trailerTypeName) => {
  return appConstants.LINKEX_ROUTE_TRAILER_TYPES.includes(trailerTypeName);
};

export const checkIfOnlyLinkexRoute = ({ routesList, trailersData, parametersData }) => {
  const isLinkexTrailerPresent = trailersData?.some((trailer) =>
    checkIfLinkexRouteTrailer(trailer.trailerTypeName)
  );
  const isSingleLinkexRouteSelected = routesList?.length === SINGLE_SELECTED_ROUTE_COUNT && routesList[0] === appConstants.LINKEX_ROUTE_NAME;
  return (
    isSingleLinkexRouteSelected &&
    isCreateFinalMileRouteEnabled(parametersData) &&
    isLinkexTrailerPresent
  );
};

export const getInitialTrailerTypeCountList = (savedTrailerCount, data) => {
  const savedCountLookup =
    savedTrailerCount?.reduce((acc, item) => {
      acc[item.trailerId] = item.trailerCount; // Map trailerId to trailerCount
      return acc;
    }, {}) || {};

  const trailerCountList = data.trailersData.map((trailer) => {
    let trailerCount = null;
    if (data.isOnlyFMERoute) {
      trailerCount = checkIfFMERouteTrailer(trailer.trailerTypeName) ? trailer.totalCount : 0;
    } else if (data.isOnlyLinkexRoute) {
      trailerCount = checkIfLinkexRouteTrailer(trailer.trailerTypeName) ? trailer.totalCount : 0;
    } else {
      trailerCount = savedCountLookup[trailer.trailerId] ?? trailer.totalCount;
    }
    return {
      trailerCount,
      trailerId: trailer.trailerId
    };
  });
  return trailerCountList;
};

export const checkIfFMERouteTrailer = (trailerTypeName) => {
  return appConstants.FME_ROUTE_TRAILER_TYPES.includes(trailerTypeName);
};

export const getGroupedDriverPreferencesListBasedOnPlanDate = (driverPreferencesList, planDate) => {
  if (!driverPreferencesList || !planDate) return [];
  const weekdayDriverCountField = getWeekdayDriverCountFieldName(planDate);

  return driverPreferencesList
    .map((driverPreference) => ({
      startTime: driverPreference.startTime,
      driverCount: driverPreference[weekdayDriverCountField] ?? driverPreference.defaultDriverCount
    }))
    .filter((driverPreference) => driverPreference.driverCount);
};

const getWeekdayDriverCountFieldName = (planDate) => {
  //Here the Date function consider the passed value as UTC
  // And it converts the date to local time zone
  const dateObject = new Date(planDate); 
  
  // Since the Date function assumed the passed value as UTC, we need to pass the timeZone as UTC to get the correct weekday name
  const weekdayName = dateObject.toLocaleString('en-US', { weekday: 'long', timeZone: 'UTC' })?.toLowerCase();
  return `${weekdayName}DriverCount`;
};

/**
 * Initial Dynamic Routing "return to terminal" checkbox (`isReturnToTerminal`).
 * When `EnableReturnToTerminalCheckedByDefault` is on and `DisableReturnToTerminalOption` is off,
 * opens checked. Otherwise uses session `savedFormData.isReturnToTerminal`, default false.
 *
 * @param {object} options
 * @param {boolean} options.isReturnToTerminalCheckedByDefault
 * @param {boolean} options.disableReturnToTerminalOption
 * @param {object} [options.savedFormData]
 */
export const getInitialIsReturnToTerminal = ({
  isReturnToTerminalCheckedByDefault,
  disableReturnToTerminalOption,
  savedFormData
}) =>
  isReturnToTerminalCheckedByDefault && !disableReturnToTerminalOption
    ? true
    : savedFormData?.isReturnToTerminal ?? false;
